import { useEffect, useState, useCallback } from 'react'
import { AlertTriangle, Activity } from 'lucide-react'
import {
  fetchAllSeries, spreadStatus, calcRiskMode,
  type SeriesData, type SeriesId, type RiskMode
} from '@/lib/fred'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL as string

interface BannerData {
  fedFunds: number | null
  t10y2y: number | null
  vix: number | null
  riskMode: RiskMode
}

function derive(data: Record<SeriesId, SeriesData>): BannerData {
  return {
    fedFunds: data.FEDFUNDS.latest,
    t10y2y: data.T10Y2Y.latest,
    vix: data.VIXCLS.latest,
    riskMode: calcRiskMode(data.VIXCLS.latest, data.BAMLH0A0HYM2, data.T10Y2Y.latest),
  }
}

export default function MacroBanner() {
  const [banner, setBanner] = useState<BannerData | null>(null)

  const load = useCallback(async () => {
    try {
      const result = await fetchAllSeries(SUPA_URL, false)
      setBanner(derive(result))
    } catch {}
  }, [])

  useEffect(() => { load() }, [load])

  if (!banner) return null

  const { fedFunds, t10y2y, vix, riskMode } = banner
  const t10y2yStatus = spreadStatus(t10y2y)
  const vixClass =
    vix == null ? 'text-gray-400' :
    vix < 15    ? 'text-green-400' :
    vix > 25    ? 'text-red-400'   : 'text-amber-400'

  const riskClass =
    riskMode === 'Risk-ON'  ? 'text-green-400' :
    riskMode === 'Risk-OFF' ? 'text-red-400'   : 'text-amber-400'

  const spreadClass =
    t10y2yStatus === 'severely_inverted' ? 'text-red-400' :
    t10y2yStatus === 'inverted'          ? 'text-amber-400' : 'text-gray-300'

  return (
    <div className="bg-black border-b border-white/10 px-4 py-1.5 flex items-center gap-1 overflow-x-auto text-xs shrink-0">
      <span className="text-gray-600 text-[10px] uppercase tracking-wider mr-2 shrink-0">FRED</span>

      {/* Fed Funds */}
      <div className="flex items-center gap-1 shrink-0 pr-3 border-r border-white/10">
        <span className="text-gray-500">Fed Funds</span>
        <span className="text-white font-bold font-mono">
          {fedFunds != null ? `${fedFunds.toFixed(2)}%` : '—'}
        </span>
      </div>

      {/* T10Y2Y */}
      <div className="flex items-center gap-1 shrink-0 px-3 border-r border-white/10">
        <span className="text-gray-500">10Y-2Y</span>
        <span className={`font-bold font-mono ${spreadClass}`}>
          {t10y2y != null ? `${t10y2y.toFixed(2)}%` : '—'}
        </span>
        {t10y2yStatus !== 'normal' && (
          <AlertTriangle size={10} className={t10y2yStatus === 'severely_inverted' ? 'text-red-400' : 'text-amber-400'} />
        )}
        {t10y2yStatus !== 'normal' && (
          <span className={`text-[9px] font-semibold ${t10y2yStatus === 'severely_inverted' ? 'text-red-400' : 'text-amber-400'}`}>
            {t10y2yStatus === 'severely_inverted' ? 'INVERSÉÉ' : 'INVERSÉE'}
          </span>
        )}
      </div>

      {/* VIX */}
      <div className="flex items-center gap-1 shrink-0 px-3 border-r border-white/10">
        <span className="text-gray-500">VIX</span>
        <span className={`font-bold font-mono ${vixClass}`}>
          {vix != null ? vix.toFixed(1) : '—'}
        </span>
      </div>

      {/* Risk mode */}
      <div className="flex items-center gap-1 shrink-0 pl-3">
        <Activity size={10} className={riskClass} />
        <span className={`font-bold ${riskClass}`}>{riskMode}</span>
      </div>
    </div>
  )
}
